import { useState } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Textarea } from './textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import {
  Plus, Trash2, ChevronUp, ChevronDown, Type, Image, Heading, Quote, 
  MousePointer, Minus, GripVertical
} from 'lucide-react';

const BLOCK_TYPES = [
  { type: 'text', label: 'Text', icon: Type, desc: 'Paragraph of text' },
  { type: 'heading', label: 'Heading', icon: Heading, desc: 'Section heading' },
  { type: 'image', label: 'Image', icon: Image, desc: 'Image with caption' },
  { type: 'quote', label: 'Quote', icon: Quote, desc: 'Blockquote' },
  { type: 'cta', label: 'CTA Button', icon: MousePointer, desc: 'Call-to-action' },
  { type: 'divider', label: 'Divider', icon: Minus, desc: 'Separator line' },
];

function BlockItem({ block, index, total, onUpdate, onDelete, onMove }) {
  const config = BLOCK_TYPES.find(t => t.type === block.type) || BLOCK_TYPES[0];
  const Icon = config.icon;
  const data = block.data || {};

  return (
    <div className="group border rounded-lg bg-white hover:shadow-sm transition-shadow">
      {/* Block header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b rounded-t-lg">
        <GripVertical className="h-4 w-4 text-gray-300" />
        <Icon className="h-4 w-4 text-gray-500" />
        <span className="text-xs font-medium text-gray-500 uppercase">{config.label}</span>
        <div className="ml-auto flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMove(index, -1)} disabled={index === 0}><ChevronUp className="h-3 w-3" /></Button>
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMove(index, 1)} disabled={index === total - 1}><ChevronDown className="h-3 w-3" /></Button>
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => onDelete(index)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
        </div>
      </div>

      {/* Block content */}
      <div className="p-3">
        {block.type === 'text' && (
          <Textarea value={data.text || ''} onChange={e => onUpdate(index, { ...block, data: { ...data, text: e.target.value } })}
            placeholder="Write your paragraph here..." rows={4} className="border-0 shadow-none resize-none focus-visible:ring-0 text-base" />
        )}

        {block.type === 'heading' && (
          <div className="flex gap-2">
            <Select value={data.level || 'h2'} onValueChange={v => onUpdate(index, { ...block, data: { ...data, level: v } })}>
              <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="h2">H2</SelectItem>
                <SelectItem value="h3">H3</SelectItem>
              </SelectContent>
            </Select>
            <Input value={data.text || ''} onChange={e => onUpdate(index, { ...block, data: { ...data, text: e.target.value } })}
              placeholder="Section heading..." className="text-xl font-bold border-0 shadow-none focus-visible:ring-0" />
          </div>
        )}

        {block.type === 'image' && (
          <div className="space-y-2">
            <Input value={data.url || ''} onChange={e => onUpdate(index, { ...block, data: { ...data, url: e.target.value } })}
              placeholder="Image URL (paste or use media library)" />
            {data.url && <img src={data.url} alt={data.alt || ''} className="max-h-48 rounded-lg object-cover" />}
            <div className="flex gap-2">
              <Input value={data.caption || ''} onChange={e => onUpdate(index, { ...block, data: { ...data, caption: e.target.value } })}
                placeholder="Caption (optional)" className="flex-1 text-sm" />
              <Select value={data.size || 'full'} onValueChange={v => onUpdate(index, { ...block, data: { ...data, size: v } })}>
                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Width</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="small">Small</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {block.type === 'quote' && (
          <div className="space-y-2 pl-4 border-l-4 border-gray-300">
            <Textarea value={data.text || ''} onChange={e => onUpdate(index, { ...block, data: { ...data, text: e.target.value } })}
              placeholder="Quote text..." rows={2} className="border-0 shadow-none resize-none focus-visible:ring-0 italic text-lg" />
            <Input value={data.author || ''} onChange={e => onUpdate(index, { ...block, data: { ...data, author: e.target.value } })}
              placeholder="— Attribution (optional)" className="border-0 shadow-none focus-visible:ring-0 text-sm text-gray-500" />
          </div>
        )}

        {block.type === 'cta' && (
          <div className="flex gap-2 items-center p-2 bg-blue-50 rounded">
            <Input value={data.text || ''} onChange={e => onUpdate(index, { ...block, data: { ...data, text: e.target.value } })}
              placeholder="Button text" className="flex-1" />
            <Input value={data.link || ''} onChange={e => onUpdate(index, { ...block, data: { ...data, link: e.target.value } })}
              placeholder="/catalog" className="flex-1" />
            <Select value={data.style || 'primary'} onValueChange={v => onUpdate(index, { ...block, data: { ...data, style: v } })}>
              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="primary">Primary</SelectItem>
                <SelectItem value="outline">Outline</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {block.type === 'divider' && (
          <hr className="my-2 border-gray-200" />
        )}
      </div>
    </div>
  );
}

export default function BlockEditor({ blocks = [], onChange }) {
  const [showMenu, setShowMenu] = useState(false);

  const addBlock = (type) => {
    const newBlock = { type, data: {} };
    if (type === 'heading') newBlock.data = { text: '', level: 'h2' };
    if (type === 'image') newBlock.data = { url: '', caption: '', alt: '', size: 'full' };
    if (type === 'cta') newBlock.data = { text: '', link: '', style: 'primary' };
    onChange([...blocks, newBlock]);
    setShowMenu(false);
  };

  const updateBlock = (index, block) => {
    const updated = [...blocks];
    updated[index] = block;
    onChange(updated);
  };

  const deleteBlock = (index) => {
    onChange(blocks.filter((_, i) => i !== index));
  };

  const moveBlock = (index, direction) => {
    const updated = [...blocks];
    const target = index + direction;
    if (target < 0 || target >= updated.length) return;
    [updated[index], updated[target]] = [updated[target], updated[index]];
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      {blocks.length === 0 && (
        <div className="border-2 border-dashed rounded-lg p-8 text-center text-gray-400">
          <Type className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No content blocks yet. Add your first block below.</p>
        </div>
      )}

      {blocks.map((block, i) => (
        <BlockItem key={i} block={block} index={i} total={blocks.length}
          onUpdate={updateBlock} onDelete={deleteBlock} onMove={moveBlock} />
      ))}

      {/* Add block */}
      <div className="relative">
        <Button type="button" variant="outline" className="w-full border-dashed" onClick={() => setShowMenu(!showMenu)}>
          <Plus className="h-4 w-4 mr-2" /> Add Block
        </Button>
        {showMenu && (
          <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg p-2 grid grid-cols-3 gap-1">
            {BLOCK_TYPES.map(bt => (
              <button key={bt.type} onClick={() => addBlock(bt.type)}
                className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 text-left text-sm">
                <bt.icon className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="font-medium">{bt.label}</p>
                  <p className="text-[10px] text-gray-400">{bt.desc}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
